import { Component, h, Host, Prop, State, Event, Listen, EventEmitter, Element, Method } from '@stencil/core';

@Component({
  tag: 'lilu-select-button',
  styleUrl: 'lilu-select-button.scss',
  shadow: true,
})
export class LiluSelectButton {
  @Prop() isOpen = false;
  @Prop() type = 'primary';
  @Prop() options: string;
  @Prop() appearance = '';
  @Prop() height = 'h-lg';
  @Prop() width = 'w-fixed';
  @Prop() font = 'regular';
  @Prop() fontsize = 'size-18 lg-size-20';
  @Prop() transform = 'capitalize';

  @State() jsonOptions: any[] = [];
  @State() currentIndex = 0;

  @Element() el: HTMLElement;

  @Event()
  optionSelected: EventEmitter<string>;

  componentDidLoad() {
    if (this.options !== undefined) {
      this.jsonOptions = JSON.parse(this.options);
    }
  }

  componentDidUpdate() {
    const selectModal: HTMLElement = this.el.shadowRoot.querySelector('.button-select-modal') as HTMLElement;
    selectModal?.focus();
  }

  close() {
    this.currentIndex = 0;
    this.isOpen = false;
    this.el?.focus();
  }

  @Listen('click', { target: 'window' })
  handleClickOutside(event: Event) {
    if (this.isOpen && !this.el.contains(event.target as Node)) {
      this.close();
    }
  }

  handleKeyDown(event: KeyboardEvent) {
    const { key } = event;
    if (this.isOpen) {
      switch (key) {
        case 'Escape':
          this.close();
          break;

        case 'ArrowUp':
          event.preventDefault();
          if (this.currentIndex > 0) {
            this.currentIndex--;
          } else {
            this.currentIndex = this.jsonOptions.length - 1;
          }
          break;

        case 'ArrowDown':
          event.preventDefault();
          if (this.currentIndex < this.jsonOptions.length - 1) {
            this.currentIndex++;
          } else {
            this.currentIndex = 0;
          }
          break;

        case 'Home':
          event.preventDefault();
          this.currentIndex = 0;
          break;

        case 'End':
          event.preventDefault();
          this.currentIndex = this.jsonOptions.length - 1;
          break;

        case 'Tab':
          this.close();
          break;

        case 'Enter':
        case ' ':
          event.preventDefault();
          this.selectOption(this.jsonOptions[this.currentIndex]?.value);
          break;

        default:
          break;
      }
    } else {
      // Accessiblity -> Space should open the dropdown when it's focused
      if (key === ' ' || key === 'Enter') {
        event.preventDefault();
        this.isOpen = true;
      }
    }
  }

  getClassString(): string {
    return `btn ${this.type} ${this.appearance} ${this.height} ${this.width}`;
  }

  @Method()
  async getSelectedOption() {
    return this.jsonOptions.find(o => o.selected === true)?.value ?? '';
  }

  getVarName(viewPort = ''): string {
    return '--selected-font-size-' + viewPort;
  }

  getFontSizesVars() {
    let fontsVariables = {};
    const fonts = this.fontsize.split(' ');
    fonts.forEach(font => {
      font = font.replace('size-', '');
      const fontDefinition = font.split('-');

      if (fontDefinition.length === 2) {
        // fontsize for a specific viewport
        fontsVariables[this.getVarName(fontDefinition[0])] = fontDefinition[1] + 'px';
      } else {
        // one fontsize
        fontsVariables['--selected-font-size'] = fontDefinition[0] + 'px';
      }
    });

    return fontsVariables;
  }

  toggelModal() {
    this.isOpen = !this.isOpen;
  }

  selectOption(value: string) {
    this.optionSelected.emit(value);
    this.close();

    // Update state of json and set currentIndex to selected option
    const updatedOptions = [...this.jsonOptions].map(option => {
      option.selected = option.value === value;
      return option;
    });
    const newIndex = updatedOptions.findIndex(o => o.value === value);
    this.currentIndex = newIndex ?? 0;
    this.jsonOptions = updatedOptions;
  }

  handleMouseOver(newIndexFromMouseInput: number) {
    this.currentIndex = newIndexFromMouseInput;
  }

  render() {
    return (
      <Host
        role="listbox"
        aria-expanded={this.isOpen.toString()}
        aria-haspopup="true"
        aria-labelledby="selectLabel"
        tabindex="0"
        class={`${this.fontsize}`}
        part={`${this.fontsize}`}
        style={this.getFontSizesVars()}
        onKeyDown={event => this.handleKeyDown(event)}
      >
        <div class={`${this.getClassString()}`} onClick={() => this.toggelModal()}>
          <span class="content">
            <slot name="start"></slot>
            <span id="selectLabel" class={`text ${this.font} size ${this.transform}`}>
              <slot></slot>
            </span>
            <slot name="end"></slot>
          </span>
        </div>
        <div class={`button-select-modal ${this.isOpen ? 'isOpen' : ''}`} tabindex={-1}>
          <ul>
            {this.jsonOptions.map((item, index) => (
              <li
                class="option"
                onPointerMove={() => this.handleMouseOver(index)}
                onClick={() => this.selectOption(item.value)}
                role="option"
                aria-selected={item.selected.toString()}
              >
                <div class={`option-container ${this.currentIndex === index ? 'active' : ''}`}>
                  <lilu-icon size="size-20" color={`item.selected && this.currentIndex === index ? 'white : 'nero'`} type="icon-checkmark-01"></lilu-icon>
                  <div>{item.text}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </Host>
    );
  }
}
